import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, Link, MenuItem, Radio, RadioGroup, Stack, Step, StepLabel, Stepper,
  Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import {
  FIELDS, TEMPLATE_CSV, buildRows, findHeaderRow, guessMapping, headerLabels,
} from './priceList';

const STEPS = ['Choose file', 'Match columns', 'Check changes', 'Done'];
const money = (v) => `$${Number(v).toFixed(2)}`;

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'eqaya-price-list-template.csv';
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Upload a supplier price list (Excel or CSV) into a merchant's catalog.
 * `endpoint` is '/merchants/catalog/import' for the merchant, or
 * '/admin/merchant-catalogs/:profileId/import' when our team does it for them.
 */
export default function PriceListImportDialog({ open, onClose, onApplied, endpoint, merchantName }) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [file, setFile] = useState(null); // { name, sheets: { [name]: rows[][] } }
  const [sheet, setSheet] = useState('');
  const [headerIndex, setHeaderIndex] = useState(0);
  const [mapping, setMapping] = useState({});
  const [defaultUnit, setDefaultUnit] = useState('each');
  const [missingAction, setMissingAction] = useState('keep');
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState('priceChanged');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(0); setError(''); setFile(null); setPreview(null); setResult(null); setMissingAction('keep');
    api.get(endpoint).then((res) => {
      const data = res.data.data;
      setSaved(data);
      if (data?.mapping?.defaultUnit) setDefaultUnit(data.mapping.defaultUnit);
    }).catch(() => setSaved(null));
  }, [open, endpoint]);

  const rows = file && sheet ? file.sheets[sheet] : [];
  const headers = rows.length ? headerLabels(rows[headerIndex]) : [];

  const selectSheet = (f, name) => {
    const r = f.sheets[name];
    const h = findHeaderRow(r);
    setSheet(name);
    setHeaderIndex(h);
    setMapping(guessMapping(headerLabels(r[h]), saved?.mapping));
  };

  const readFile = async (picked) => {
    if (!picked) return;
    setError('');
    setBusy(true);
    try {
      const wb = XLSX.read(await picked.arrayBuffer(), { type: 'array' });
      const sheets = {};
      wb.SheetNames.forEach((n) => {
        sheets[n] = XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, blankrows: false, defval: '', raw: true });
      });
      const names = wb.SheetNames.filter((n) => sheets[n].length > 1);
      if (!names.length) throw new Error('empty');
      const f = { name: picked.name, sheets, names };
      setFile(f);
      // Start on the sheet with the most rows: price lists rarely live on a cover sheet.
      selectSheet(f, names.reduce((a, b) => (sheets[b].length > sheets[a].length ? b : a)));
      setStep(1);
    } catch (e) {
      setError('That file could not be read. Save it as .xlsx or .csv and try again.');
    } finally {
      setBusy(false);
    }
  };

  const dataRows = rows.length ? buildRows(rows, headerIndex, headers, mapping) : [];
  const sample = dataRows.slice(0, 5);
  const mappedFields = FIELDS.filter((f) => mapping[f.key]);

  const runPreview = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`${endpoint}/preview`, {
        fileName: file.name, mapping, rows: dataRows, missingAction, defaultUnit,
      });
      setPreview(res.data.data);
      const s = res.data.data.summary;
      setTab(s.priceChanged ? 'priceChanged' : s.new ? 'new' : s.errors ? 'errors' : 'missing');
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.error || 'The price list could not be checked. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await api.post(`${endpoint}/${preview.importId}/apply`, {});
      setResult(res.data.data.summary);
      setStep(3);
      if (onApplied) onApplied();
    } catch (e) {
      setError(e.response?.data?.error || 'The price list could not be applied. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const s = preview?.summary;
  const tabs = s ? [
    { key: 'priceChanged', label: `Price changes (${s.priceChanged})` },
    { key: 'new', label: `New (${s.new})` },
    { key: 'updated', label: `Other changes (${s.updated})` },
    { key: 'errors', label: `Skipped (${s.errors})` },
    { key: 'missing', label: `Not in this list (${s.missing})` },
  ] : [];
  const tabRows = preview?.samples?.[tab] || [];
  const tabTotal = s ? { priceChanged: s.priceChanged, new: s.new, updated: s.updated, errors: s.errors, missing: s.missing }[tab] : 0;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Import price list{merchantName ? ` for ${merchantName}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((l) => <Step key={l}><StepLabel>{l}</StepLabel></Step>)}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {step === 0 && (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary">
              Upload the price list exactly as it comes out of the stock system: Excel (.xlsx, .xls) or CSV.
              You’ll match its columns and see every change before anything goes live.
            </Typography>
            <Button variant="contained" component="label" startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <UploadFile />} disabled={busy}>
              Choose file
              <input hidden type="file" accept=".xlsx,.xls,.csv" data-testid="price-list-file"
                onChange={(e) => readFile(e.target.files[0])} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              No list to hand? <Link component="button" type="button" onClick={downloadTemplate}>Download a template</Link>.
              Up to 10,000 products per file.
            </Typography>
            {saved?.imports?.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                Last import: {saved.imports[0].file_name || 'price list'},{' '}
                {new Date(saved.imports[0].applied_at).toLocaleDateString()}. Its column choices will be reused.
              </Typography>
            )}
          </Stack>
        )}

        {step === 1 && file && (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {file.names.length > 1 && (
                <TextField select size="small" label="Sheet" value={sheet} onChange={(e) => selectSheet(file, e.target.value)} sx={{ minWidth: 180 }}>
                  {file.names.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </TextField>
              )}
              <TextField select size="small" label="Header row" value={headerIndex}
                onChange={(e) => { const h = Number(e.target.value); setHeaderIndex(h); setMapping(guessMapping(headerLabels(rows[h]), saved?.mapping)); }}
                sx={{ minWidth: 180 }}>
                {rows.slice(0, 15).map((r, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <MenuItem key={i} value={i}>Row {i + 1}: {(r || []).filter(Boolean).slice(0, 3).join(', ').slice(0, 40)}</MenuItem>
                ))}
              </TextField>
              <Chip label={`${dataRows.length.toLocaleString()} products`} sx={{ alignSelf: 'center' }} />
            </Stack>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {FIELDS.map((f) => (
                <TextField key={f.key} select size="small" label={`${f.label}${f.required ? ' *' : ''}`}
                  value={mapping[f.key] || ''} helperText={f.hint}
                  onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value || undefined }))}>
                  <MenuItem value=""><em>Not in this file</em></MenuItem>
                  {headers.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                </TextField>
              ))}
              {!mapping.unit && (
                <TextField size="small" label="Unit for every product" value={defaultUnit}
                  onChange={(e) => setDefaultUnit(e.target.value)} />
              )}
            </Box>

            {sample.length > 0 && mappedFields.length > 0 && (
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>{mappedFields.map((f) => <TableCell key={f.key}>{f.label}</TableCell>)}</TableRow>
                  </TableHead>
                  <TableBody>
                    {sample.map((r) => (
                      <TableRow key={r.row}>
                        {mappedFields.map((f) => <TableCell key={f.key}>{String(r[f.key] ?? '')}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <FormControl>
              <Typography variant="subtitle2">Products already on Eqaya that are not in this list</Typography>
              <RadioGroup row value={missingAction} onChange={(e) => setMissingAction(e.target.value)}>
                <FormControlLabel value="keep" control={<Radio size="small" />} label="Leave them as they are" />
                <FormControlLabel value="out_of_stock" control={<Radio size="small" />} label="Mark them out of stock" />
              </RadioGroup>
            </FormControl>
          </Stack>
        )}

        {step === 2 && s && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip color="primary" label={`${s.new} new`} />
              <Chip color="warning" label={`${s.priceChanged} price changes (${s.priceUp} up, ${s.priceDown} down)`} />
              <Chip label={`${s.updated} other changes`} />
              <Chip variant="outlined" label={`${s.unchanged} unchanged`} />
              {s.errors > 0 && <Chip color="error" label={`${s.errors} skipped`} />}
              {s.missing > 0 && <Chip variant="outlined" label={`${s.missing} not in this list`} />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Applying confirms every price in this list as current. Skipped rows are left out; fix them in the file and import again.
              {s.missing > 0 && (preview.missingAction === 'out_of_stock'
                ? ` ${s.missing} products not in this list will be marked out of stock.`
                : ` ${s.missing} products not in this list stay as they are.`)}
            </Typography>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
              {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{tab === 'missing' ? 'Code' : 'Row'}</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">{tab === 'errors' ? '' : 'Price'}</TableCell>
                    {tab === 'errors' && <TableCell>Why</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tabRows.map((r) => (tab === 'missing' ? (
                    <TableRow key={r.id}>
                      <TableCell>{r.sku || '—'}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{r.unit}</TableCell>
                      <TableCell align="right">{money(r.unit_price)}</TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={r.row}>
                      <TableCell>{r.row}</TableCell>
                      <TableCell>{r.data.name || '—'}{r.data.sku ? <Typography variant="caption" color="text.secondary" display="block">{r.data.sku}</Typography> : null}</TableCell>
                      <TableCell>{r.data.unit}</TableCell>
                      <TableCell align="right">
                        {tab === 'priceChanged' && <><s>{money(r.oldPrice)}</s>{' → '}</>}
                        {r.data.unit_price !== undefined ? money(r.data.unit_price) : ''}
                      </TableCell>
                      {tab === 'errors' && <TableCell sx={{ color: 'error.main' }}>{r.errors.join('; ')}</TableCell>}
                    </TableRow>
                  )))}
                  {tabRows.length === 0 && (
                    <TableRow><TableCell colSpan={5} sx={{ color: 'text.secondary' }}>Nothing here.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {tabTotal > tabRows.length && (
              <Typography variant="caption" color="text.secondary">Showing the first {tabRows.length} of {tabTotal.toLocaleString()}.</Typography>
            )}
          </Stack>
        )}

        {step === 3 && result && (
          <Alert severity="success">
            Price list applied: {result.new} added, {result.priceChanged} prices changed, {result.updated} updated,
            {' '}{result.unchanged} confirmed unchanged
            {result.markedOutOfStock ? `, ${result.markedOutOfStock} marked out of stock` : ''}
            {result.errors ? `. ${result.errors} rows were skipped.` : '.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {step === 3 ? (
          <Button variant="contained" onClick={onClose}>Close</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            {step === 1 && (
              <Button variant="contained" onClick={runPreview}
                disabled={busy || !mapping.name || !mapping.unitPrice || dataRows.length === 0}>
                {busy ? 'Checking…' : 'Check changes'}
              </Button>
            )}
            {step === 2 && (
              <>
                <Button onClick={() => setStep(1)} disabled={busy}>Back</Button>
                <Button variant="contained" onClick={apply}
                  disabled={busy || (s.new + s.priceChanged + s.updated + s.unchanged + (preview.missingAction === 'out_of_stock' ? s.missing : 0)) === 0}>
                  {busy ? 'Applying…' : 'Apply to catalog'}
                </Button>
              </>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
